import { useEffect } from 'react'
import { KeycloakService } from '@nv/auth'
import { useVoterUserContext } from '@nv/contexts'
import { VoterAuthService } from '@nv/services'
import { beforeUnload, nxConfig } from '@nv/utils'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export const useVoterKeyCloakAuth = () => {
  const { initialized, setInitialized, setVoterUser, voterUser, setAuthChecked } = useVoterUserContext()
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language
  const pathname = window.location.pathname

  useEffect(() => {
    const initializeSSO = async () => {
      if (!initialized) {
        KeycloakService.initKeycloak(() => {
          setInitialized(true)
        })
      }
      setTimeout(() => {
        setInitialized(true)
      }, 5000)
    }
    if (nxConfig.PUBLIC_URL == null) throw new Error('Missing .env file')
    else initializeSSO()
  }, [])

  const navigate = useNavigate()

  const setLoggedInLocal = async () => {
    const tokenParsed = KeycloakService?.getTokenParsed()

    if (tokenParsed) {
      if (pathname.startsWith('/voting')) navigate('/voting/voter/dashboard')

      setVoterUser({
        ...tokenParsed,
      })
      const user: any = await VoterAuthService.getUserInfo()
      if (user) {
        const votingChannel = user.votingChannel ?? tokenParsed.votingChannel
        setVoterUser({
          ...tokenParsed,
          ...user,
          votingChannel: votingChannel,
          username: tokenParsed.preferred_username,
          adminView: votingChannel === 'online',
        })

        if (votingChannel === 'online') {
          const receipt = sessionStorage.getItem('receipt')
          if (receipt) {
            const eventId = sessionStorage.getItem('eventId')
            const event = await VoterAuthService.getEventById(eventId)
            if (event) {
              const hasVoted = user?.votedInEvents?.includes(eventId)
              navigate('/election', { state: { event, hasVoted, receipt } })
            } else {
              navigate('/voting/voter/dashboard')
            }
          } else {
            navigate('/voting/voter/dashboard')
          }
          window.addEventListener('beforeunload', beforeUnload)
        }
        setSavedKeycloakLanguage()
      } else {
        clearLocalSession()
      }
    }

    setAuthChecked(true)
  }

  const clearLocalSession = () => {
    setAuthChecked(true)
    setVoterUser(null)
    window.removeEventListener('beforeunload', beforeUnload)
    // only unauthenticated pages that user can navigate directly too
    // add landing page
    if (
      !pathname.includes('/voting/voter-login') &&
      !pathname.includes('/voting/voter/login') &&
      !pathname.startsWith('/voting/voter-onboarding/phone-verify') &&
      !pathname.includes('/voting/voter-auth/reset-password') &&
      !pathname.includes('/voting/voter/single-sign-on')
    ) {
      navigate('/')
    }
  }

  const checkAuth = async () => {
    // is logged in
    if (initialized && KeycloakService?.isLoggedIn()) {
      if (KeycloakService?.isLoggedIn() && voterUser?.votingChannel === 'inPerson') {
        clearLocalSession()
      } else {
        setLoggedInLocal()
      }
    }
    if (initialized && !KeycloakService?.isLoggedIn()) {
      clearLocalSession()
    }
  }

  useEffect(() => {
    checkAuth()
  }, [initialized, KeycloakService?.isLoggedIn()])

  // set language saved in app
  const setSavedKeycloakLanguage = () => {
    if (KeycloakService.isLoggedIn()) {
      VoterAuthService.updateUserLanguage(currentLanguage)
    }
  }
}
